import moment from 'moment';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IAffiliate } from 'src/models';
import { useCopyToClipboard } from 'usehooks-ts';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DoneIcon from '@mui/icons-material/Done';
import { IconButton, TableCell, TableRow } from '@mui/material';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

type Props = {
  affiliate: IAffiliate;
  index: number;
};

export const AffiliateRow: FC<Props> = ({ affiliate, index }) => {
  const [, copy] = useCopyToClipboard();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isCopiedIcon, setCopiedIcon] = useState(false);
  const { t } = useTranslation();

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const pop = (address: string) => {
    return (
      <Popover
        id="mouse-over-popover"
        sx={{
          pointerEvents: 'none',
        }}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        {' '}
        <Typography sx={{ p: 1 }}>{address}</Typography>
      </Popover>
    );
  };

  const startCopyPipeline = () => {
    setCopiedIcon(true);
    setTimeout(() => {
      setCopiedIcon(false);
    }, 1000);
  };

  return (
    <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
      <TableCell component="th" scope="row">
        {affiliate.externalAffiliate ? (
          <Tooltip title={t('affiliate.overview.table.externalAffiliate')}>
            <ArrowOutwardIcon />
          </Tooltip>
        ) : (
          <Tooltip title={t('affiliate.overview.table.internalAffiliate')}>
            <AssignmentIndIcon />
          </Tooltip>
        )}
      </TableCell>
      <TableCell component="th" scope="row">
        {affiliate.affiliateId}
      </TableCell>
      <TableCell component="th" scope="row">
        {affiliate.name}
      </TableCell>
      <TableCell align="left">{affiliate.email}</TableCell>
      <TableCell
        align="left"
        sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '200px' }}
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
      >
        {affiliate?.address ?? ''}
      </TableCell>
      {affiliate?.address && pop(affiliate.address)}
      <TableCell align="left">{affiliate?.birthDate ? moment(affiliate?.birthDate)?.format('DD.MM.YYYY') : ''}</TableCell>
      <TableCell align="left">
        {affiliate.affiliateLink && (
          <IconButton
            onClick={() => {
              copy(affiliate?.affiliateLink ?? '');
              startCopyPipeline();
            }}
          >
            {!isCopiedIcon && <ContentCopyIcon fontSize="small" />}
            {isCopiedIcon && <DoneIcon fontSize="small" />}
          </IconButton>
        )}
        {affiliate.affiliateLink}
      </TableCell>
    </TableRow>
  );
};
